/**
 * The Astrally mark — a zodiac wheel with an inscribed chart diamond.
 *
 * The two shapes are the product's thesis in one symbol: the CIRCLE is the
 * Western zodiac wheel, the inscribed DIAMOND is the Indian chart square.
 * Together they say "both systems, one chart", which no other app mark does.
 *
 * Deliberately region-neutral. An earlier draft used the North Indian kundli
 * grid, but that format is regional — South India draws a 4×4 square grid and
 * the east draws its own — so a literal North Indian chart would have marked a
 * pan-India product as northern. A circle plus a rotated square belongs to
 * everyone. (Both chart styles are supported inside the app; see
 * features/charts.)
 *
 * It also replaces the original four-pointed sparkle, which read as the Gemini
 * logo and as generic "AI" decoration.
 *
 * The gold cell is the top segment — house 1, the lagna: the house Astrally is
 * most careful about, and the one it refuses to guess when the birth time is
 * unknown.
 *
 * Geometry (viewBox 0 0 64 64): circle centre (32,32) r 26; diamond vertices
 * at the circle's cardinal points (32,6) (58,32) (32,58) (6,32).
 */

export interface AstrallyMarkProps {
  /** Rendered width/height in px. */
  size?: number;
  /** Stroke colour for the wheel and chart lines. */
  color?: string;
  /** Fill for the lagna (house 1) segment. Pass null to leave it empty. */
  lagnaColor?: string | null;
  /** Stroke weight in viewBox units — 4.2 matches the master artwork. */
  weight?: number;
  className?: string;
  title?: string;
}

export function AstrallyMark({
  size = 40,
  color = 'var(--accent, #8b7cf6)',
  lagnaColor = 'var(--gold, #e8b86d)',
  weight = 4.2,
  className,
  title = 'Astrally',
}: AstrallyMarkProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      role="img"
      aria-label={title}
      className={className}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Lagna segment (house 1) — drawn first so the lines sit on top. Its
          lower vertex lands exactly on the centre, never past it. */}
      {lagnaColor && <path d="M32 6.3 L44.85 19.15 L32 32 L19.15 19.15 Z" fill={lagnaColor} />}

      {/* The Western zodiac wheel. */}
      <circle cx="32" cy="32" r="25.7" stroke={color} strokeWidth={weight} />

      {/* The Indian chart square, inscribed — vertices on the wheel. */}
      <path
        d="M32 6.3 L57.7 32 L32 57.7 L6.3 32 Z"
        stroke={color}
        strokeWidth={weight}
        strokeLinejoin="miter"
      />

      {/* Centre point — the native, around whom the chart is cast. */}
      <circle cx="32" cy="32" r="3.2" fill={color} />
    </svg>
  );
}
